import React, { useEffect, useMemo, useState } from 'react';
import { addDoc, collection, doc, query, updateDoc, where } from 'firebase/firestore';
import { getDownloadURL, ref } from 'firebase/storage';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { auth, db, storage } from '../firebase';
import PageShell from './PageShell';
import { buildCaseAccessLink } from '../utils/caseAccess';
import {
  CloseIcon,
  DraftingIcon,
  EyeIcon,
  LockIcon,
  MessageIcon,
  PlusIcon,
  UnlockIcon,
  WhatsAppIcon,
} from './AppIcons';
import LoadingState from './LoadingState';
import { syncCaseAccessComment, syncCaseAccessPayment, syncCaseAccessRecord } from '../utils/clientAccessRecords';
import { createLifecycleStep, formatLifecycleDate, isHearingLifecycleStep, sortLifecycleForCase } from '../utils/lifecycle';
import { useFirestoreCollection, useFirestoreDocument } from '../utils/firestoreCache';

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(value || 0);

const emptyPaymentForm = {
  amount: '',
  date: new Date().toISOString().split('T')[0],
  description: '',
  stage: '',
  requestedFromClient: true,
};

const buildShareMessage = (caseRecord) =>
  `iAdvocate has shared your case updates for ${caseRecord.case_number}. Open your case link here: ${buildCaseAccessLink(
    caseRecord.client_access_token
  )}`;

const buildWhatsAppShareLink = (caseRecord) =>
  `https://wa.me/?text=${encodeURIComponent(buildShareMessage(caseRecord))}`;

const buildSmsShareLink = (caseRecord) =>
  `sms:?&body=${encodeURIComponent(buildShareMessage(caseRecord))}`;

const buildPreviewLink = (token) => `${buildCaseAccessLink(token)}?preview=1`;

const formatDateTime = (value, locale = 'en-IN') => {
  if (!value) return '';
  const nextDate = new Date(value);
  if (Number.isNaN(nextDate.getTime())) return '';
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(nextDate);
};

const CaseDetails = () => {
  const { t } = useTranslation();
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [caseRecord, setCaseRecord] = useState(null);
  const [payments, setPayments] = useState([]);
  const [paymentForm, setPaymentForm] = useState(emptyPaymentForm);
  const [selectedLifecycleTitle, setSelectedLifecycleTitle] = useState('');
  const [selectedLifecycleEta, setSelectedLifecycleEta] = useState('');
  const [selectedLifecycleType, setSelectedLifecycleType] = useState('general');
  const [selectedLifecycleDate, setSelectedLifecycleDate] = useState('');
  const [selectedLifecycleNotes, setSelectedLifecycleNotes] = useState('');
  const [activePanel, setActivePanel] = useState('');
  const [expandedLifecycleStep, setExpandedLifecycleStep] = useState('');
  const [comments, setComments] = useState([]);
  const [caseDocuments, setCaseDocuments] = useState([]);
  const [clientDocuments, setClientDocuments] = useState([]);
  const [commentDraft, setCommentDraft] = useState('');
  const locale = t('preferredLocale', { defaultValue: 'en-IN' });
  const paymentsPanelOpen = activePanel === 'payments' || activePanel === 'payments-add';
  const notesPanelOpen = activePanel === 'notes' || activePanel === 'notes-add';
  const advocateId = auth.currentUser?.uid || '';
  const caseState = useFirestoreDocument({
    enabled: Boolean(caseId),
    docFactory: () => doc(db, 'cases', caseId),
    queryKey: [caseId || '', 'case-details'],
  });
  const paymentsState = useFirestoreCollection({
    enabled: Boolean(caseState.data?.case_number),
    queryFactory: () =>
      caseState.data?.client_access_token
        ? collection(db, 'client_access', caseState.data.client_access_token, 'payments')
        : query(
            collection(db, 'payments'),
            where('advocate_id', '==', advocateId),
            where('case_id', '==', caseState.data.case_number)
          ),
    queryKey: [advocateId, caseState.data?.client_access_token || '', caseState.data?.case_number || '', 'case-payments'],
  });
  const commentsState = useFirestoreCollection({
    enabled: Boolean(caseState.data?.case_number),
    queryFactory: () =>
      caseState.data?.client_access_token
        ? collection(db, 'client_access', caseState.data.client_access_token, 'comments')
        : query(
            collection(db, 'comments'),
            where('advocate_id', '==', advocateId),
            where('case_id', '==', caseState.data.case_number)
          ),
    queryKey: [advocateId, caseState.data?.client_access_token || '', caseState.data?.case_number || '', 'case-comments'],
  });
  const documentsState = useFirestoreCollection({
    enabled: Boolean(caseState.data?.case_number),
    queryFactory: () =>
      caseState.data?.client_access_token
        ? collection(db, 'client_access', caseState.data.client_access_token, 'documents')
        : query(
            collection(db, 'documents'),
            where('advocate_id', '==', advocateId),
            where('case_id', '==', caseState.data.case_number)
          ),
    queryKey: [advocateId, caseState.data?.client_access_token || '', caseState.data?.case_number || '', 'case-documents'],
  });

  useEffect(() => {
    setCaseRecord(caseState.data);
    setPayments(paymentsState.data);
    setComments(
      commentsState.data
        .slice()
        .sort((left, right) => new Date(right.created_at || 0) - new Date(left.created_at || 0))
    );
  }, [caseState.data, commentsState.data, paymentsState.data]);

  useEffect(() => {
    let active = true;
    const resolveDocuments = async () => {
      const resolvedDocuments = await Promise.all(
        documentsState.data.map(async (documentItem) => {
          const nextDocument = { ...documentItem };
          if (!nextDocument.url && nextDocument.storage_path) {
            try {
              nextDocument.url = await getDownloadURL(ref(storage, nextDocument.storage_path));
            } catch (error) {
              nextDocument.url = '';
            }
          }
          return nextDocument;
        })
      );
      if (!active) {
        return;
      }
      setCaseDocuments(
        resolvedDocuments
          .filter((item) => item.uploaded_by_role !== 'client')
          .sort((left, right) => new Date(right.created_at || right.synced_at || 0) - new Date(left.created_at || left.synced_at || 0))
      );
      setClientDocuments(
        resolvedDocuments
          .filter((item) => item.uploaded_by_role === 'client')
          .sort((left, right) => new Date(right.created_at || right.synced_at || 0) - new Date(left.created_at || left.synced_at || 0))
      );
    };

    resolveDocuments();
    return () => {
      active = false;
    };
  }, [documentsState.data]);

  const progressLabel = useMemo(() => {
    const lifecycle = caseRecord?.lifecycle || [];
    const completed = lifecycle.filter((step) => step.status === 'done').length;
    return `${completed}/${lifecycle.length || 1} milestones complete`;
  }, [caseRecord]);

  const updateLifecycleStatus = async (stepId, nextStatus) => {
    if (!caseRecord) return;
    const lifecycle = (caseRecord.lifecycle || []).map((step) =>
      step.id === stepId ? { ...step, status: nextStatus } : step
    );
    const sortedLifecycle = sortLifecycleForCase(lifecycle);
    setCaseRecord((current) => ({ ...current, lifecycle: sortedLifecycle }));
    await updateDoc(doc(db, 'cases', caseRecord.id), { lifecycle: sortedLifecycle });
    await syncCaseAccessRecord({ ...caseRecord, lifecycle: sortedLifecycle });
  };

  const updateLifecycleField = async (stepId, key, value) => {
    if (!caseRecord) return;
    const lifecycle = (caseRecord.lifecycle || []).map((step) =>
      step.id === stepId ? { ...step, [key]: value } : step
    );
    setCaseRecord((current) => ({ ...current, lifecycle }));
    await updateDoc(doc(db, 'cases', caseRecord.id), { lifecycle });
    await syncCaseAccessRecord({ ...caseRecord, lifecycle });
  };

  const addLifecycleStep = async () => {
    if (!caseRecord || !selectedLifecycleTitle.trim()) return;
    const currentLifecycle = caseRecord.lifecycle || [];
    const firstPendingIndex = currentLifecycle.findIndex((step) => step.status === 'pending');
    const insertAt = firstPendingIndex === -1 ? currentLifecycle.length : firstPendingIndex;
    const nextStep = createLifecycleStep({
      id: `step-${Date.now()}`,
      title: selectedLifecycleTitle.trim(),
      eta: selectedLifecycleEta,
      scheduledDate: selectedLifecycleDate,
      stageType: selectedLifecycleType,
      notes: selectedLifecycleNotes,
      status: 'pending',
    });
    const lifecycle = [
      ...currentLifecycle.slice(0, insertAt),
      nextStep,
      ...currentLifecycle.slice(insertAt),
    ];
    setCaseRecord((current) => ({ ...current, lifecycle }));
    await updateDoc(doc(db, 'cases', caseRecord.id), { lifecycle });
    await syncCaseAccessRecord({ ...caseRecord, lifecycle });
    setSelectedLifecycleTitle('');
    setSelectedLifecycleEta('');
    setSelectedLifecycleDate('');
    setSelectedLifecycleNotes('');
    setExpandedLifecycleStep(nextStep.id);
    setActivePanel('');
  };

  const toggleClientAccess = async () => {
    if (!caseRecord) return;
    await updateDoc(doc(db, 'cases', caseRecord.id), {
      client_access_enabled: !caseRecord.client_access_enabled,
    });
    await syncCaseAccessRecord({
      ...caseRecord,
      client_access_enabled: !caseRecord.client_access_enabled,
    });
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    if (!caseRecord) return;

    const paymentPayload = {
      advocate_id: auth.currentUser?.uid,
      case_id: caseRecord.case_number,
      amount: parseFloat(paymentForm.amount),
      date: paymentForm.date,
      description: paymentForm.description,
      stage: paymentForm.stage || 'Case fee',
      status: paymentForm.requestedFromClient ? 'Requested' : 'Paid',
      requested_from_client: paymentForm.requestedFromClient,
      client_access_token: caseRecord.client_access_token,
    };

    const paymentRef = await addDoc(collection(db, 'payments'), paymentPayload);
    await syncCaseAccessPayment(caseRecord.client_access_token, paymentPayload, paymentRef.id);

    setPaymentForm(emptyPaymentForm);
    setActivePanel('');
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!caseRecord || !commentDraft.trim()) return;

    const payload = {
      advocate_id: auth.currentUser?.uid,
      case_id: caseRecord.case_number,
      author_role: 'advocate',
      author_name: auth.currentUser?.displayName || 'Advocate',
      message: commentDraft.trim(),
      created_at: new Date().toISOString(),
      client_access_token: caseRecord.client_access_token,
    };

    const commentRef = await addDoc(collection(db, 'comments'), payload);
    await syncCaseAccessComment(caseRecord.client_access_token, payload, commentRef.id);
    setCommentDraft('');
    setActivePanel('');
  };

  const loading =
    caseState.loadingInitial ||
    paymentsState.loadingInitial ||
    commentsState.loadingInitial ||
    documentsState.loadingInitial;
  const refreshing =
    caseState.refreshing ||
    paymentsState.refreshing ||
    commentsState.refreshing ||
    documentsState.refreshing;

  const toggleLifecycleStep = (stepId) => {
    setExpandedLifecycleStep((current) => {
      const next = current === stepId ? '' : stepId;
      if (next) {
        setActivePanel('');
      }
      return next;
    });
  };

  const togglePanel = (panelKey) => {
    setActivePanel((current) => {
      const next = current === panelKey ? '' : panelKey;
      if (next) {
        setExpandedLifecycleStep('');
      }
      return next;
    });
  };

  const openDocument = (documentItem) => {
    if (!documentItem?.url) return;
    window.open(documentItem.url, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <PageShell title={t('caseDetails')} subtitle={t('loadingMatterDetails')} showBack>
        <LoadingState label={t('loadingMatterDetails')} />
      </PageShell>
    );
  }

  if (!caseRecord) {
    return (
      <PageShell title={t('caseDetails')} subtitle={t('loadingMatterDetails')} showBack>
        <section className="panel">
          <p className="empty-state">{t('caseNotFound')}</p>
        </section>
      </PageShell>
    );
  }

  return (
    <PageShell
      title={caseRecord.case_number}
      subtitle={t('caseDetailsSubtitle')}
      showBack
      actions={
        <div className="header-icon-group">
          <a
            className="icon-button icon-button--whatsapp"
            href={buildWhatsAppShareLink(caseRecord)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t('shareOnWhatsApp')}
            title={t('shareOnWhatsApp')}
          >
            <WhatsAppIcon className="app-icon" />
          </a>
          <a
            className="icon-button"
            href={buildSmsShareLink(caseRecord)}
            aria-label={t('shareBySms')}
            title={t('shareBySms')}
          >
            <MessageIcon className="app-icon" />
          </a>
          <a
            className="icon-button"
            href={buildPreviewLink(caseRecord.client_access_token)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t('previewClientCaseView')}
            title={t('previewClientCaseView')}
          >
            <EyeIcon className="app-icon" />
          </a>
          <button
            type="button"
            className="icon-button"
            aria-label={t('openDraftingAssistant')}
            title={t('openDraftingAssistant')}
            onClick={() => navigate(`/drafting?caseId=${caseRecord.id}`)}
          >
            <DraftingIcon className="app-icon" />
          </button>
          <button
            type="button"
            className={`icon-button${caseRecord.client_access_enabled ? '' : ' icon-button--danger'}`}
            aria-label={caseRecord.client_access_enabled ? t('pauseClientAccess') : t('enableClientAccess')}
            title={caseRecord.client_access_enabled ? t('pauseClientAccess') : t('enableClientAccess')}
            onClick={toggleClientAccess}
          >
            {caseRecord.client_access_enabled ? <UnlockIcon className="app-icon" /> : <LockIcon className="app-icon" />}
          </button>
        </div>
      }
    >
      {refreshing ? <p className="helper-text">{t('refreshingWorkspace', { defaultValue: 'Refreshing from your latest saved data...' })}</p> : null}
      <section className="hero-card case-hero">
        <div>
          <p className="eyebrow">{t('clientLabel')}</p>
          <h2>{caseRecord.client_name}</h2>
          <p>{caseRecord.client_phone || caseRecord.client_email || t('clientContactMissing')}</p>
        </div>
        <div className="case-hero__meta">
          <span className="badge">{caseRecord.status}</span>
          <span className="case-hero__progress">{progressLabel}</span>
        </div>
      </section>

      <section className="panel">
        <div className="section-heading">
          <div>
            <p className="eyebrow">{t('matterBrief')}</p>
            <h2>{t('caseDetails')}</h2>
          </div>
        </div>
        <div className="details-grid">
          <article className="record-item">
            <div>
              <strong>{t('summaryLabel')}</strong>
              <p>{caseRecord.summary || t('noSummaryAdded')}</p>
            </div>
          </article>
          <article className="record-item">
            <div>
              <strong>{t('nextStepShort')}</strong>
              <p>{caseRecord.next_step || t('noNextStepYet')}</p>
            </div>
          </article>
          <article className="record-item">
            <div>
              <strong>{t('clientLinkLabel')}</strong>
              <p>{caseRecord.client_access_enabled ? t('clientLinkActive') : t('clientLinkDisabled')}</p>
            </div>
            <a
              className="icon-button"
              href={buildPreviewLink(caseRecord.client_access_token)}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t('previewClientCaseView')}
              title={t('previewClientCaseView')}
            >
              <EyeIcon className="app-icon" />
            </a>
          </article>
        </div>
      </section>

      <section className="panel">
        <div className="section-heading">
          <div>
            <p className="eyebrow">{t('lifecycle')}</p>
            <h2>{t('progressControl')}</h2>
          </div>
          <button
            type="button"
            className="icon-button icon-button--accent"
            aria-label={activePanel === 'lifecycle-add' ? t('closeAddStageForm') : t('addLifecycleStep')}
            title={activePanel === 'lifecycle-add' ? t('closeAddStageForm') : t('addLifecycleStep')}
            onClick={() => togglePanel('lifecycle-add')}
          >
            {activePanel === 'lifecycle-add' ? <CloseIcon className="app-icon" /> : <PlusIcon className="app-icon" />}
          </button>
        </div>
        {activePanel === 'lifecycle-add' ? (
        <>
        <div className="planning-row">
          <input
            type="text"
            value={selectedLifecycleTitle}
            onChange={(e) => setSelectedLifecycleTitle(e.target.value)}
            placeholder={t('stepTitle')}
          />
          <select value={selectedLifecycleType} onChange={(e) => setSelectedLifecycleType(e.target.value)}>
            <option value="general">{t('generalStage')}</option>
            <option value="hearing">{t('hearingStage')}</option>
          </select>
          <input
            type="month"
            value={selectedLifecycleEta}
            onChange={(e) => setSelectedLifecycleEta(e.target.value)}
          />
          <input
            type="date"
            value={selectedLifecycleDate}
            onChange={(e) => setSelectedLifecycleDate(e.target.value)}
          />
        </div>
        <div className="form-group top-space">
          <label>{t('stageNotes')}</label>
          <textarea
            value={selectedLifecycleNotes}
            onChange={(e) => setSelectedLifecycleNotes(e.target.value)}
            placeholder={t('stageNotesPlaceholder')}
          />
        </div>
        <button type="button" className="button" onClick={addLifecycleStep}>
          {t('addLifecycleStep')}
        </button>
        </>
        ) : (
          <p className="empty-state">{t('lifecycleFormHint')}</p>
        )}
        <div className="lifecycle-editor">
          {(caseRecord.lifecycle || []).map((step, index) => (
            <div key={step.id} className="lifecycle-row lifecycle-row--flat">
              <button
                type="button"
                className="lifecycle-row__summary"
                onClick={() => toggleLifecycleStep(step.id)}
              >
                <div>
                  <strong>{t('stepNumber', { count: index + 1 })}</strong>
                  <p>{step.title}</p>
                  {isHearingLifecycleStep(step) && step.scheduled_date ? (
                    <p className="case-status-text">{t('hearingOn')} {formatLifecycleDate(step.scheduled_date)}</p>
                  ) : null}
                </div>
                <div className="record-item__action">
                  <span className="badge">{step.status}</span>
                  {expandedLifecycleStep === step.id ? <CloseIcon className="app-icon" /> : <PlusIcon className="app-icon" />}
                </div>
              </button>
              {expandedLifecycleStep === step.id ? (
              <div className="planning-stack lifecycle-row__editor">
                <input
                  type="text"
                  value={step.title}
                  onChange={(e) => updateLifecycleField(step.id, 'title', e.target.value)}
                  placeholder={t('stepTitle')}
                />
                <select
                  value={step.stage_type || 'general'}
                  onChange={(e) => updateLifecycleField(step.id, 'stage_type', e.target.value)}
                >
                  <option value="general">{t('generalStage')}</option>
                  <option value="hearing">{t('hearingStage')}</option>
                </select>
                <input
                  type="month"
                  value={step.eta || ''}
                  onChange={(e) => updateLifecycleField(step.id, 'eta', e.target.value)}
                />
                <input
                  type="date"
                  value={step.scheduled_date || ''}
                  onChange={(e) => updateLifecycleField(step.id, 'scheduled_date', e.target.value)}
                />
                <textarea
                  value={step.notes || ''}
                  onChange={(e) => updateLifecycleField(step.id, 'notes', e.target.value)}
                  placeholder={t('stageNotesPlaceholder')}
                />
                <select value={step.status} onChange={(e) => updateLifecycleStatus(step.id, e.target.value)}>
                  <option value="pending">{t('tentative')}</option>
                  <option value="in_progress">{t('inProgress')}</option>
                  <option value="done">{t('done')}</option>
                </select>
              </div>
              ) : null}
            </div>
          ))}
        </div>
      </section>

      <section className="panel">
        <div className="section-heading">
          <div>
            <p className="eyebrow">{t('payments')}</p>
            <h2>{t('requestOrRecordFees')}</h2>
          </div>
          <button
            type="button"
            className="icon-button icon-button--accent"
            aria-label={paymentsPanelOpen ? t('closePaymentHistory') : t('openPaymentHistory')}
            title={paymentsPanelOpen ? t('closePaymentHistory') : t('openPaymentHistory')}
            onClick={() => togglePanel('payments')}
          >
            {paymentsPanelOpen ? <CloseIcon className="app-icon" /> : <PlusIcon className="app-icon" />}
          </button>
        </div>
        {!paymentsPanelOpen ? (
          <p className="empty-state">{t('feeFormHint')}</p>
        ) : null}
        {paymentsPanelOpen ? (
          <>
          <div className="record-list top-space">
            {payments.length === 0 ? (
              <p className="empty-state">{t('paymentsEmpty')}</p>
            ) : (
              payments.map((payment) => (
                <article key={payment.id} className="record-item record-item--stack">
                  <div>
                    <strong>{payment.description || payment.stage || t('caseFee')}</strong>
                    <p>{payment.stage || t('caseFee')}</p>
                    <p className="record-item__meta">{payment.date}</p>
                  </div>
                  <span className="badge">{formatCurrency(payment.amount)} | {payment.status || 'Paid'}</span>
                </article>
              ))
            )}
          </div>
          <button
            type="button"
            className="button button--secondary top-space"
            onClick={() => setActivePanel((current) => (current === 'payments-add' ? 'payments' : 'payments-add'))}
          >
            {activePanel === 'payments-add' ? t('closePaymentForm') : t('addPayment')}
          </button>
          </>
        ) : null}
        {activePanel === 'payments-add' ? (
          <form onSubmit={handlePaymentSubmit} className="top-space">
            <div className="form-grid">
              <div className="form-group">
                <label>{t('amount')}:</label>
                <input
                  type="number"
                  placeholder={t('amountInInr')}
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm((current) => ({ ...current, amount: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label>{t('date')}:</label>
                <input
                  type="date"
                  value={paymentForm.date}
                  onChange={(e) => setPaymentForm((current) => ({ ...current, date: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label>{t('stage')}:</label>
                <input
                  type="text"
                  placeholder="Filing, hearing, drafting, etc."
                  value={paymentForm.stage}
                  onChange={(e) => setPaymentForm((current) => ({ ...current, stage: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>{t('paymentMode')}:</label>
                <select
                  value={paymentForm.requestedFromClient ? 'request' : 'recorded'}
                  onChange={(e) =>
                    setPaymentForm((current) => ({
                      ...current,
                      requestedFromClient: e.target.value === 'request',
                    }))
                  }
                >
                  <option value="request">{t('requestFromClient')}</option>
                  <option value="recorded">{t('recordReceived')}</option>
                </select>
              </div>
              <div className="form-group full-span">
                <label>{t('description')}:</label>
                <input
                  type="text"
                  placeholder={t('paymentPurpose')}
                  value={paymentForm.description}
                  onChange={(e) => setPaymentForm((current) => ({ ...current, description: e.target.value }))}
                  required
                />
              </div>
            </div>
            <button type="submit" className="button">
              {paymentForm.requestedFromClient ? t('requestPayment') : t('savePayment')}
            </button>
          </form>
        ) : null}
      </section>

      <section className="panel">
        <div className="section-heading">
          <div>
            <p className="eyebrow">{t('comments')}</p>
            <h2>{t('caseNotes')}</h2>
          </div>
          <button
            type="button"
            className="icon-button icon-button--accent"
            aria-label={notesPanelOpen ? t('closeCaseNotesHistory') : t('openCaseNotesHistory')}
            title={notesPanelOpen ? t('closeCaseNotesHistory') : t('openCaseNotesHistory')}
            onClick={() => togglePanel('notes')}
          >
            {notesPanelOpen ? <CloseIcon className="app-icon" /> : <PlusIcon className="app-icon" />}
          </button>
        </div>
        {!notesPanelOpen ? (
          <p className="empty-state">{t('caseNotesHint')}</p>
        ) : null}
        {notesPanelOpen ? (
          <>
          {comments.length === 0 ? (
            <p className="empty-state top-space">{t('caseNotesEmpty')}</p>
          ) : (
            <div className="record-list top-space">
              {comments.map((commentItem) => (
                <article key={commentItem.id} className="record-item record-item--stack">
                  <div>
                    <strong>{commentItem.author_name || commentItem.author_role}</strong>
                    <p className="record-item__meta">{formatDateTime(commentItem.created_at, locale)}</p>
                    <p>{commentItem.message}</p>
                  </div>
                  <span className="badge">{commentItem.author_role}</span>
                </article>
              ))}
            </div>
          )}
          <button
            type="button"
            className="button button--secondary top-space"
            onClick={() => setActivePanel((current) => (current === 'notes-add' ? 'notes' : 'notes-add'))}
          >
            {activePanel === 'notes-add' ? t('closeCaseNotesForm') : t('addCaseNote')}
          </button>
          </>
        ) : null}
        {activePanel === 'notes-add' ? (
          <form onSubmit={handleCommentSubmit} className="top-space">
            <div className="form-group">
              <label>{t('addCaseNote')}</label>
              <textarea
                value={commentDraft}
                onChange={(e) => setCommentDraft(e.target.value)}
                placeholder={t('addCaseNotePlaceholder')}
                required
              />
            </div>
            <button type="submit" className="button">{t('saveCaseNote')}</button>
          </form>
        ) : null}
      </section>

      <section className="panel">
        <div className="section-heading">
          <div>
            <p className="eyebrow">{t('documents')}</p>
            <h2>{t('caseDocuments')}</h2>
          </div>
          <button
            type="button"
            className="icon-button icon-button--accent"
            aria-label={activePanel === 'case-documents' ? t('collapseCaseDocuments') : t('expandCaseDocuments')}
            title={activePanel === 'case-documents' ? t('collapseCaseDocuments') : t('expandCaseDocuments')}
            onClick={() => togglePanel('case-documents')}
          >
            {activePanel === 'case-documents' ? <CloseIcon className="app-icon" /> : <PlusIcon className="app-icon" />}
          </button>
        </div>
        {activePanel === 'case-documents' ? (
          caseDocuments.length === 0 ? (
            <p className="empty-state">{t('caseDocumentsEmpty')}</p>
          ) : (
            <div className="record-list">
              {caseDocuments.map((documentItem) => (
                <article key={documentItem.id} className="record-item">
                  <div>
                    <strong>{documentItem.name}</strong>
                    <p>{documentItem.type || t('generalFile')}</p>
                  </div>
                  <div className="record-item__action">
                    {documentItem.url ? (
                      <button type="button" className="button button--secondary" onClick={() => openDocument(documentItem)}>
                        {t('open')}
                      </button>
                    ) : null}
                    {documentItem.source_drafting_session_id ? (
                      <button
                        type="button"
                        className="button button--ghost"
                        onClick={() => navigate(`/drafting?sessionId=${documentItem.source_drafting_session_id}`)}
                      >
                        {t('editInDrafting')}
                      </button>
                    ) : null}
                  </div>
                </article>
              ))}
            </div>
          )
        ) : (
          <p className="empty-state">{t('caseDocumentsHint')}</p>
        )}
      </section>

      <section className="panel">
        <div className="section-heading">
          <div>
            <p className="eyebrow">{t('documents')}</p>
            <h2>{t('clientSharedDocuments')}</h2>
          </div>
          <button
            type="button"
            className="icon-button icon-button--accent"
            aria-label={activePanel === 'client-documents' ? t('collapseClientDocuments') : t('expandClientDocuments')}
            title={activePanel === 'client-documents' ? t('collapseClientDocuments') : t('expandClientDocuments')}
            onClick={() => togglePanel('client-documents')}
          >
            {activePanel === 'client-documents' ? <CloseIcon className="app-icon" /> : <PlusIcon className="app-icon" />}
          </button>
        </div>
        {activePanel === 'client-documents' ? (
          clientDocuments.length === 0 ? (
            <p className="empty-state">{t('clientDocumentsEmpty')}</p>
          ) : (
            <div className="record-list">
              {clientDocuments.map((documentItem) => (
                <article key={documentItem.id} className="record-item">
                  <div>
                    <strong>{documentItem.name}</strong>
                    <p>{documentItem.type || t('generalFile')}</p>
                  </div>
                  <a className="inline-link" href={documentItem.url} target="_blank" rel="noopener noreferrer">
                    {t('open')}
                  </a>
                </article>
              ))}
            </div>
          )
        ) : (
          <p className="empty-state">{t('clientDocumentsHint')}</p>
        )}
      </section>
    </PageShell>
  );
};

export default CaseDetails;
